import { useState, useEffect } from "react";
import axios from "axios";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

/**
 * Heart-shaped toggle so members can bookmark a business.
 * - Signed-out users get a friendly "sign in to save" toast (no navigation
 *   hijack — they can still browse freely).
 * - Signed-in users toggle instantly with an optimistic update; failures
 *   revert the icon so the UI never lies about server state.
 *
 * Props:
 *  - businessId   (required)   business_id to favorite
 *  - initial      (bool)       initial "already favorited" state
 *  - size         ("sm"|"md")  visual size (default md)
 *  - stopClickBubble  (bool)   set true when nested inside a <Link> so the
 *                              parent link doesn't navigate on heart click
 *  - onChange(bool)            called with the new favorited state
 */
export default function FavoriteButton({
  businessId,
  initial = false,
  size = "md",
  stopClickBubble = false,
  onChange,
}) {
  const { user } = useAuth();
  const [favorited, setFavorited] = useState(Boolean(initial));
  const [busy, setBusy] = useState(false);

  // Keep the visible state in sync with the parent's async-hydrated `initial`
  // prop (e.g. BusinessProfilePage fetches /favorites/ids after mount and only
  // then knows whether this business is already favorited).
  useEffect(() => {
    setFavorited(Boolean(initial));
  }, [initial]);

  const dim = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  const icon = size === "sm" ? "w-4 h-4" : "w-5 h-5";

  const handleClick = async (e) => {
    if (stopClickBubble) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (busy) return;

    if (!user) {
      toast.info("Sign in to save businesses to your favorites");
      return;
    }

    const next = !favorited;
    setFavorited(next); // optimistic
    setBusy(true);
    try {
      if (next) {
        await axios.post(`${API}/favorites/${businessId}`, {}, { withCredentials: true });
      } else {
        await axios.delete(`${API}/favorites/${businessId}`, { withCredentials: true });
      }
      onChange?.(next);
    } catch (err) {
      setFavorited(!next); // revert
      toast.error(err.response?.data?.detail || "Couldn't update favorite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`${dim} inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-sm border border-gray-200 transition-all hover:scale-105 disabled:opacity-60`}
      title={favorited ? "Remove from favorites" : "Save to favorites"}
      aria-label={favorited ? "Remove from favorites" : "Save to favorites"}
      data-testid={`favorite-btn-${businessId}`}
      data-favorited={favorited ? "true" : "false"}
    >
      <Heart
        className={`${icon} transition-colors ${
          favorited ? "fill-red-500 text-red-500" : "text-gray-500"
        }`}
      />
    </button>
  );
}
